import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, Volume2, Loader2, Home, RotateCcw, ArrowUpRight } from "lucide-react";
import { CATEGORIES, getItems, speechText, MAX_LEVEL } from "@/data/content";
import { BIGKIDS_CATEGORIES, getBKItems, isBigKidsCategory } from "@/data/bigkids";
import { useSpeak } from "@/hooks/useSpeak";
import { updateProgress, setLevel } from "@/lib/api";
import { celebrate, stopConfetti } from "@/lib/celebrate";

export default function LearnPage({ profile, refreshProfile }) {
  const { category } = useParams();
  const navigate = useNavigate();
  const { speak, speaking } = useSpeak();
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [done, setDone] = useState(false);
  const celebratedRef = useRef(false);
  const learnedRef = useRef(new Set());

  const isBK = isBigKidsCategory(category);
  const meta =
    CATEGORIES.find((c) => c.key === category) ||
    BIGKIDS_CATEGORIES.find((c) => c.key === category);
  const level = profile.levels?.[category] || 1;
  const items = isBK ? getBKItems(category, level) : getItems(category, level);
  const item = items[index];
  const isQuiz = !!(meta && meta.quiz);
  const ageBand = ["Ages 3–5", "Ages 4–6", "Ages 5–6"][level - 1] || "Ages 3–5";

  useEffect(() => {
    setIndex(0);
    setDone(false);
    celebratedRef.current = false;
    const levelKeys = (isBK ? getBKItems(category, level) : getItems(category, level)).map((it) => it.k);
    learnedRef.current = new Set(
      (profile.progress?.[category] || []).filter((k) => levelKeys.includes(k))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, level]);

  if (!meta || !item) {
    navigate("/home");
    return null;
  }
  if (meta.premium && !profile.premium) {
    navigate("/home");
    return null;
  }

  const recordProgress = (itemKey) => {
    learnedRef.current.add(itemKey);
    if (learnedRef.current.size >= items.length && !celebratedRef.current) {
      celebratedRef.current = true;
      celebrate();
      setTimeout(() => setDone(true), 400);
      setTimeout(
        () => speak(`Hooray! You finished Level ${level} of ${meta.title}! Great job!`),
        1000
      );
    }
    updateProgress(profile.id, category, itemKey)
      .then((p) => refreshProfile(p.id))
      .catch(() => {});
  };

  const handleSpeak = () => {
    speak(speechText(category, item));
    recordProgress(item.k);
  };

  const replay = () => {
    stopConfetti();
    learnedRef.current = new Set();
    setDone(false);
    celebratedRef.current = false;
    setDir(1);
    setIndex(0);
  };

  const goNextLevel = async () => {
    stopConfetti();
    const nextLvl = Math.min(MAX_LEVEL, level + 1);
    try {
      const p = await setLevel(profile.id, category, nextLvl);
      await refreshProfile(p.id);
    } catch (e) {}
    // level change triggers the reset effect; also reset here for immediacy
    setDone(false);
    celebratedRef.current = false;
    setDir(1);
    setIndex(0);
  };

  const go = (delta) => {
    setDir(delta);
    setIndex((i) => (i + delta + items.length) % items.length);
  };

  const isColor = category === "colors";
  const isNumber = category === "numbers";

  return (
    <div
      className="app-shell flex flex-col"
      style={{ background: `${meta.color}12` }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 pt-6">
        <button
          data-testid="back-button"
          onClick={() => navigate("/home")}
          className="toy-btn w-14 h-14 rounded-2xl bg-white border-4 border-slate-900/10 flex items-center justify-center"
          style={{ boxShadow: "0 5px 0 0 rgba(15,23,42,0.1)" }}
        >
          <ArrowLeft size={28} strokeWidth={3} className="text-slate-700" />
        </button>
        <div className="flex items-center gap-2">
          <span className="text-3xl">{meta.emoji}</span>
          <span className="font-fredoka text-2xl font-bold text-slate-800">
            {meta.title}
          </span>
        </div>
        <div
          className="px-4 py-2 rounded-full font-bold text-white text-sm"
          style={{ backgroundColor: meta.color }}
          data-testid="card-counter"
        >
          {index + 1}/{items.length}
        </div>
      </div>

      {isQuiz ? (
        <QuizView
          key={`${category}-${index}`}
          item={item}
          meta={meta}
          index={index}
          total={items.length}
          onCorrect={() => recordProgress(item.k)}
          onNext={() => go(1)}
          onPrev={() => go(-1)}
          speak={speak}
        />
      ) : (
        <>
          {/* Card area */}
          <div className="flex-1 flex items-center justify-between px-3 sm:px-8 gap-3">
            <NavArrow dir="left" onClick={() => go(-1)} color={meta.color} testid="prev-button" />

            <div className="flex-1 max-w-xl flex justify-center">
              <AnimatePresence mode="wait" custom={dir}>
                <motion.div
                  key={`${category}-${index}`}
                  custom={dir}
                  initial={{ opacity: 0, x: dir * 80, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: dir * -80, scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 260, damping: 24 }}
                  className="relative w-full bg-white rounded-[2.5rem] border-4 border-slate-900/10 p-8 sm:p-10 flex flex-col items-center gap-6"
                  style={{ boxShadow: `0 12px 0 0 ${meta.shadow}` }}
                  data-testid="learning-card"
                >
                  {!isBK && (
                    <span
                      data-testid="age-ribbon"
                      className="absolute top-4 left-4 bg-yellow-400 text-slate-900 font-bold text-xs uppercase tracking-widest px-3 py-1.5 rounded-full shadow"
                    >
                      {ageBand}
                    </span>
                  )}
                  {/* Visual */}
                  {isColor ? (
                    <div
                      className="w-44 h-44 sm:w-56 sm:h-56 rounded-3xl border-4 border-slate-900/10"
                      style={{ backgroundColor: item.hex }}
                      data-testid="color-swatch"
                    />
                  ) : isNumber ? (
                    <div className="flex flex-col items-center gap-4">
                      <span className="font-fredoka text-[7rem] sm:text-[9rem] leading-none font-bold" style={{ color: meta.color }}>
                        {item.k}
                      </span>
                      <div className="flex flex-wrap justify-center gap-1 max-w-xs">
                        {parseInt(item.k, 10) <= 20 &&
                          Array.from({ length: parseInt(item.k, 10) }).map((_, i) => (
                            <span key={i} className="text-3xl sm:text-4xl">
                              {item.emoji}
                            </span>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="text-8xl sm:text-9xl">{item.emoji}</span>
                      {category === "alphabet" && (
                        <div className="flex items-baseline gap-2">
                          <span className="font-fredoka text-6xl sm:text-7xl font-bold" style={{ color: meta.color }}>
                            {item.k}
                          </span>
                          {level === 1 && (
                            <span className="font-fredoka text-4xl sm:text-5xl font-bold text-slate-300">
                              {item.k.toLowerCase()}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Word */}
                  <span className="font-fredoka text-4xl sm:text-5xl font-bold text-slate-900 text-center">
                    {item.word}
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>

            <NavArrow dir="right" onClick={() => go(1)} color={meta.color} testid="next-button" />
          </div>

          {/* Speaker (outside AnimatePresence so it always targets the current item) */}
          <div className="flex justify-center pb-4">
            <button
              data-testid="speaker-button"
              onClick={handleSpeak}
              className="toy-btn flex items-center gap-3 text-white font-fredoka text-2xl font-bold px-10 py-5 rounded-full"
              style={{ backgroundColor: meta.color, boxShadow: `0 8px 0 0 ${meta.shadow}` }}
            >
              {speaking ? (
                <Loader2 size={34} strokeWidth={3} className="animate-spin" />
              ) : (
                <Volume2 size={34} strokeWidth={3} />
              )}
              Tap to Hear
            </button>
          </div>
        </>
      )}

      <div className="h-8" />

      <AnimatePresence>
        {done && (
          <motion.div
            className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="celebration-modal"
          >
            <motion.div
              initial={{ scale: 0.7, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.7, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 16 }}
              className="bg-white rounded-[2.5rem] border-4 border-slate-900/10 p-10 w-full max-w-sm text-center"
              style={{ boxShadow: `0 14px 0 0 ${meta.shadow}` }}
            >
              <motion.div
                animate={{ rotate: [0, -12, 12, -8, 8, 0] }}
                transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.4 }}
                className="text-7xl mb-4"
              >
                🎉
              </motion.div>
              <h2 className="font-fredoka text-4xl font-bold text-slate-900 mb-2">
                Hooray!
              </h2>
              <p className="text-xl font-bold text-slate-500 mb-8">
                You finished Level {level} of {meta.title}!
              </p>
              <div className="flex flex-col gap-3">
                {level < MAX_LEVEL && (
                  <button
                    data-testid="celebration-nextlevel-button"
                    onClick={goNextLevel}
                    className="toy-btn flex items-center justify-center gap-2 bg-green-500 text-white font-fredoka text-2xl font-bold py-4 rounded-2xl"
                    style={{ boxShadow: "0 6px 0 0 rgba(21,128,61,1)" }}
                  >
                    <ArrowUpRight size={26} strokeWidth={3} /> Try Level {level + 1}
                  </button>
                )}
                <button
                  data-testid="celebration-replay-button"
                  onClick={replay}
                  className="toy-btn flex items-center justify-center gap-2 text-white font-fredoka text-2xl font-bold py-4 rounded-2xl"
                  style={{ backgroundColor: meta.color, boxShadow: `0 6px 0 0 ${meta.shadow}` }}
                >
                  <RotateCcw size={26} strokeWidth={3} /> Play Again
                </button>
                <button
                  data-testid="celebration-home-button"
                  onClick={() => {
                    stopConfetti();
                    navigate("/home");
                  }}
                  className="toy-btn flex items-center justify-center gap-2 bg-slate-100 text-slate-700 font-fredoka text-2xl font-bold py-4 rounded-2xl"
                  style={{ boxShadow: "0 6px 0 0 rgba(15,23,42,0.12)" }}
                >
                  <Home size={26} strokeWidth={3} /> Home
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NavArrow({ dir, onClick, color, testid }) {
  const Icon = dir === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="toy-btn shrink-0 w-[58px] h-[58px] sm:w-[72px] sm:h-[72px] rounded-full bg-white border-4 border-slate-900/10 flex items-center justify-center"
      style={{ boxShadow: `0 6px 0 0 ${color}` }}
    >
      <Icon size={40} strokeWidth={3} style={{ color }} />
    </button>
  );
}

function QuizView({ item, meta, onCorrect, onNext, speak }) {
  const [picked, setPicked] = useState(null);
  const [solved, setSolved] = useState(false);

  useEffect(() => {
    setPicked(null);
    setSolved(false);
    speak(item.q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.k]);

  const choose = (opt) => {
    if (solved) return;
    setPicked(opt);
    if (opt === item.a) {
      setSolved(true);
      speak("Correct! Great job!");
      onCorrect();
      setTimeout(() => onNext(), 1300);
    } else {
      speak("Try again!");
      setTimeout(() => setPicked(null), 600);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 gap-8" data-testid="quiz-card">
      <motion.div
        key={item.k}
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 240, damping: 22 }}
        className="w-full max-w-xl bg-white rounded-[2.5rem] border-4 border-slate-900/10 p-8 sm:p-10 text-center"
        style={{ boxShadow: `0 12px 0 0 ${meta.shadow}` }}
      >
        <p className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-3">
          Solve it!
        </p>
        <p
          className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 leading-snug"
          data-testid="quiz-question"
        >
          {item.q}
        </p>
      </motion.div>

      <div className="w-full max-w-xl grid grid-cols-3 gap-3 sm:gap-4">
        {item.options.map((opt) => {
          const isRight = solved && opt === item.a;
          const isWrong = picked === opt && opt !== item.a;
          return (
            <button
              key={opt}
              data-testid={`quiz-option-${opt}`}
              onClick={() => choose(opt)}
              disabled={solved}
              className={`toy-btn font-fredoka text-3xl sm:text-4xl font-bold py-6 rounded-3xl border-4 border-white text-white ${
                isWrong ? "animate-pulse" : ""
              }`}
              style={{
                backgroundColor: isRight ? "#22C55E" : isWrong ? "#94A3B8" : meta.color,
                boxShadow: `0 8px 0 0 ${isRight ? "rgba(21,128,61,1)" : meta.shadow}`,
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>

      {solved && (
        <p className="font-fredoka text-2xl font-bold text-green-600" data-testid="quiz-feedback">
          Correct! 🎉
        </p>
      )}
      <div className="pb-4" />
    </div>
  );
}

